import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { CheckCircle2, Clock, CreditCard, Loader2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { useSuspenseGetSubscription } from "@/app/billing/api/hooks";
import {
  hasScheduledChangeToRaw,
  useScheduledChangeHelpers,
} from "@/app/billing/api/use-scheduled-change-helpers";
import { isPlanUpgrade } from "@/app/billing/upgrade/utils";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

const POLLING_INTERVAL_MS = 3000; // 3 seconds

export const ProcessingPage = () => {
  const navigate = useNavigate({
    from: "/orgs/$organizationId/projects/$projectId/billing",
  });
  const { organizationId } = useOrgProjectRouteContext();
  const { planId } = useParams({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/billing/upgrade/processing/$planId",
  });

  const search = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/billing/upgrade/processing/$planId",
  });

  const { data: subscription } = useSuspenseGetSubscription(organizationId, {
    // Poll every 3 seconds until the backend reflects the new plan.
    refetchInterval: (query) => {
      // Stop polling once the plan matches the expected planId
      if (query.state.data?.plan?.external_plan_id === planId) {
        return false;
      }
      // Otherwise keep polling every 3 seconds
      return POLLING_INTERVAL_MS;
    },
  });

  const { hasScheduledChangeTo, nextScheduledChange } =
    useScheduledChangeHelpers(organizationId, {
      // Poll every 3 seconds until we find a scheduled change to the expected plan
      refetchInterval: (query) => {
        // Stop polling once we find a matching scheduled change using the utility function
        if (hasScheduledChangeToRaw(query.state.data, planId)) {
          return false;
        }
        // Otherwise keep polling every 3 seconds
        return POLLING_INTERVAL_MS;
      },
    });

  // When the subscription has the desired plan redirect to overview
  useEffect(() => {
    if (subscription?.plan?.external_plan_id === planId) {
      const originalPlanId = search.originalPlanId || null;
      const isUpgrade = isPlanUpgrade(originalPlanId, planId);

      if (isUpgrade) {
        toast.success("Your plan has been upgraded successfully!");
      } else {
        toast.success("Your plan has been changed successfully!");
      }

      navigate({
        to: "./overview",
      });
    }
  }, [subscription, planId, search.originalPlanId, navigate]);

  // Check for scheduled changes that match the expected plan
  useEffect(() => {
    if (hasScheduledChangeTo(planId) && nextScheduledChange) {
      const originalPlanId = search.originalPlanId || null;
      const isUpgrade = isPlanUpgrade(originalPlanId, planId);

      if (isUpgrade) {
        toast.success("Your plan upgrade has been scheduled successfully!");
      } else {
        toast.success("Your plan change has been scheduled successfully!");
      }

      navigate({
        to: "./overview",
      });
    }
  }, [
    hasScheduledChangeTo,
    nextScheduledChange,
    planId,
    search.originalPlanId,
    navigate,
  ]);

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <div className="w-full max-w-lg">
        <div className="rounded-2xl border border-border/50 bg-card/50 p-8 shadow-xl backdrop-blur-sm">
          <div className="space-y-6 text-center">
            {/* Animated loader with pulse effect */}
            <div className="relative mx-auto flex h-16 w-16 items-center justify-center">
              <div className="absolute inset-0 animate-ping rounded-full bg-green-400/20" />
              <div className="absolute inset-2 animate-pulse rounded-full bg-green-400/30" />
              <Loader2 className="relative z-10 h-8 w-8 animate-spin text-green-500" />
            </div>

            {/* Main heading */}
            <div className="space-y-2">
              <h1 className="font-semibold text-2xl text-foreground leading-tight">
                Processing Your Plan Change
              </h1>
              <p className="text-muted-foreground">
                We're updating your subscription in the background
              </p>
            </div>

            {/* Progress steps */}
            <div className="space-y-4 py-6">
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-500" />
                <span className="text-foreground">
                  Payment processed successfully
                </span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin text-green-500" />
                </div>
                <span className="text-foreground">
                  Updating your subscription
                </span>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <Clock className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Activating new features
                </span>
              </div>
            </div>

            {/* Info card */}
            <div className="space-y-2 rounded-lg bg-muted/30 p-4 text-left">
              <div className="flex items-center gap-2 font-medium text-foreground text-sm">
                <CreditCard className="h-4 w-4" />
                What's happening?
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                This usually takes 10 to 60 seconds. You can safely keep this
                tab open or close it. Your subscription will update
                automatically.
              </p>
            </div>

            {/* Subtle animation dots */}
            <div className="flex justify-center gap-1 pt-4">
              <div className="h-2 w-2 animate-bounce rounded-full bg-green-400 [animation-delay:-0.3s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-green-400 [animation-delay:-0.15s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-green-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
